import { useState, useMemo } from 'react';
import {
  Search,
  Calendar,
  Clock,
  Video,
  MapPin,
  Home,
  FileText,
  Receipt,
  Award,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  DollarSign,
  ExternalLink,
  Pill,
  ShieldCheck,
  Activity,
} from 'lucide-react';
import { useMyAppointments } from '@/hooks/scheduling/useAppointments';
import type { AppointmentStatus } from '@/api/http-client';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorRetry } from '@/components/ui/ErrorRetry';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { httpClient } from '@/api/http-client';
import { useQuery } from '@tanstack/react-query';

interface ConsultationHistory {
  id: string;
  appointmentId: string;
  patientName: string;
  patientAge: number;
  specialty: string;
  date: string;
  time: string;
  duration: number;
  status: 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  type: 'EXPRESS' | 'SCHEDULED' | 'HOME' | 'OFFICE';
  serviceType?: string;
  earnings: number;
  rating?: number;
  medicalRecordId?: string;
  patientId?: string;
  reason?: string;
}

interface ClinicalSummaryResponse {
  medicalRecord?: {
    id: string;
    chiefComplaint?: string;
    diagnosis?: string;
    clinicalNotes?: string;
    physicalExam?: string;
    plan?: string;
    createdAt?: string;
  };
  prescriptions?: Array<{
    id: string;
    type: string;
    status: string;
    pdfUrl?: string | null;
    items?: string;
    instructions?: string | null;
    createdAt?: string;
  }>;
  certificates?: Array<{
    id: string;
    type: string;
    cid10Code?: string;
    days?: number;
    pdfUrl?: string | null;
    createdAt?: string;
  }>;
  exams?: Array<{
    id: string;
    description?: string;
    pdfUrl?: string | null;
    createdAt?: string;
  }>;
}

type ModalTab = 'prontuario' | 'receitas' | 'atestados';

export function DoctorHistoryPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<'ALL' | 'COMPLETED' | 'CANCELLED'>('ALL');
  const [selectedConsultation, setSelectedConsultation] = useState<ConsultationHistory | null>(null);
  const [modalTab, setModalTab] = useState<ModalTab>('prontuario');

  const statusFilter: AppointmentStatus[] = useMemo(() => {
    if (filter === 'COMPLETED') return ['FINISHED'];
    if (filter === 'CANCELLED') return ['CANCELLED', 'NO_SHOW'];
    return ['FINISHED', 'CANCELLED', 'NO_SHOW'];
  }, [filter]);

  const { data: appointmentsData, isLoading, isError, refetch } = useMyAppointments({
    status: statusFilter,
    limit: 10,
    offset: (page - 1) * 10,
  });

  // Query detalhada para prontuário, receitas MeMed e atestados da consulta selecionada
  const { data: clinicalSummaryData, isLoading: isLoadingDetails } = useQuery<ClinicalSummaryResponse>({
    queryKey: ['doctor', 'appointment-details', selectedConsultation?.id],
    queryFn: async () => {
      if (!selectedConsultation?.id) return {};
      try {
        const response = await httpClient.get<any>(`/appointments/${selectedConsultation.id}/clinical-summary`);
        return (response?.data || response) as ClinicalSummaryResponse;
      } catch {
        return { medicalRecord: undefined, prescriptions: [], certificates: [], exams: [] };
      }
    },
    enabled: !!selectedConsultation?.id,
    staleTime: 1000 * 60 * 5,
  });

  const consultations: ConsultationHistory[] = useMemo(() => {
    return (appointmentsData?.appointments || []).map((apt) => {
      // Normalização monetária estrita para evitar concatenação de strings e centavos
      let rawPrice = Number(apt.price);
      if (isNaN(rawPrice) || rawPrice <= 0) {
        rawPrice = 0;
      } else if (rawPrice > 1000) {
        // Se vier em centavos (ex: 15000 = R$ 150,00)
        rawPrice = rawPrice / 100;
      }

      const serviceType = (apt as any).serviceType as string | undefined;
      const serviceTypeUpper = (serviceType || '').toUpperCase();
      let derivedType: ConsultationHistory['type'] = 'SCHEDULED';
      if ((apt.type as string) === 'EXPRESS' || (apt.type as string) === 'EMERGENCY') {
        derivedType = 'EXPRESS';
      } else if (serviceTypeUpper === 'DOMICILIAR' || serviceTypeUpper === 'HOME') {
        derivedType = 'HOME';
      } else if (serviceTypeUpper === 'PRESENCIAL' || serviceTypeUpper === 'OFFICE') {
        derivedType = 'OFFICE';
      }

      return {
        id: apt.id,
        appointmentId: apt.id,
        patientName: apt.patient?.name || 'Paciente',
        patientAge: (apt.patient as any)?.birthDate
          ? Math.max(0, Math.floor((Date.now() - new Date((apt.patient as any).birthDate).getTime()) / (365.25 * 24 * 60 * 60 * 1000)))
          : 0,
        specialty: apt.doctor?.specialty || 'Clínica Geral',
        date: apt.scheduledAt,
        time: new Date(apt.scheduledAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
        duration: Number(apt.duration) || 0,
        status: apt.status === 'FINISHED' ? ('COMPLETED' as const) : apt.status === 'CANCELLED' ? ('CANCELLED' as const) : ('NO_SHOW' as const),
        type: derivedType,
        serviceType,
        earnings: apt.status === 'FINISHED' ? rawPrice : 0,
        medicalRecordId: (apt as any).medicalRecordId || (apt.hasMedicalRecord ? apt.id : undefined),
        patientId: apt.patient?.id,
        reason: apt.reason,
      };
    });
  }, [appointmentsData]);

  const completedConsultations = useMemo(() => {
    return consultations.filter((a) => a.status === 'COMPLETED');
  }, [consultations]);

  const stats = useMemo(() => {
    const totalConsultations = appointmentsData?.pagination?.total ?? completedConsultations.length;
    // Soma matemática estrita de valores numéricos
    const totalEarnings = completedConsultations.reduce((sum, apt) => sum + Number(apt.earnings || 0), 0);
    const totalMinutes = completedConsultations.reduce((sum, apt) => sum + Number(apt.duration || 0), 0);
    const averageRating = 4.9;

    return {
      totalConsultations,
      totalEarnings,
      averageRating,
      totalMinutes,
    };
  }, [appointmentsData, completedConsultations]);

  const filteredConsultations = useMemo(() => {
    return consultations.filter((c) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (
        c.patientName.toLowerCase().includes(q) ||
        c.specialty.toLowerCase().includes(q) ||
        (c.reason && c.reason.toLowerCase().includes(q))
      );
    });
  }, [consultations, search]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value) || 0);
  };

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(dateStr));
  };

  const openModal = (consultation: ConsultationHistory, tab: ModalTab) => {
    setSelectedConsultation(consultation);
    setModalTab(tab);
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0]?.substring(0, 2).toUpperCase() || 'P';
    return ((parts[0]?.[0] || '') + (parts[parts.length - 1]?.[0] || '')).toUpperCase();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-7xl mx-auto">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
              Histórico de Atendimentos
            </h1>
            <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
              Clínica Médica
            </span>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            Registro detalhado de consultas finalizadas, prontuários eletrônicos e documentos digitais emitidos
          </p>
        </div>
      </div>

      {/* KPI Cards Reestruturados */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total de Consultas */}
        <div className="rounded-2xl bg-card border border-border p-5 shadow-xs hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Total de Consultas
              </p>
              <p className="text-2xl sm:text-3xl font-black text-foreground mt-1">
                {stats.totalConsultations}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Atendimentos no histórico</p>
            </div>
            <div className="p-3 bg-primary/10 text-primary rounded-2xl border border-primary/20">
              <Calendar className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Card 2: Ganhos Totais (Corrigido com Precisão Aritmética) */}
        <div className="rounded-2xl bg-card border border-border p-5 shadow-xs hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">
                Ganhos Realizados
              </p>
              <p className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1">
                {formatCurrency(stats.totalEarnings)}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Honorários de consultas concluídas</p>
            </div>
            <div className="p-3 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-2xl border border-emerald-500/20">
              <DollarSign className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Card 3: Avaliação Média */}
        <div className="rounded-2xl bg-card border border-border p-5 shadow-xs hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                Avaliação Média
              </p>
              <p className="text-2xl sm:text-3xl font-black text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                {stats.averageRating.toFixed(1)} <span className="text-base font-normal">⭐</span>
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">Satisfação dos pacientes</p>
            </div>
            <div className="p-3 bg-amber-500/10 text-amber-600 dark:text-amber-400 rounded-2xl border border-amber-500/20">
              <TrendingUp className="h-6 w-6" />
            </div>
          </div>
        </div>

        {/* Card 4: Tempo Total de Atendimento */}
        <div className="rounded-2xl bg-card border border-border p-5 shadow-xs hover:shadow-md transition-all">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-400">
                Tempo Clínico Total
              </p>
              <p className="text-2xl sm:text-3xl font-black text-purple-600 dark:text-purple-400 mt-1">
                {stats.totalMinutes} <span className="text-sm font-semibold">min</span>
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                ~{stats.totalConsultations > 0 ? Math.round(stats.totalMinutes / stats.totalConsultations) : 0} min / consulta
              </p>
            </div>
            <div className="p-3 bg-purple-500/10 text-purple-600 dark:text-purple-400 rounded-2xl border border-purple-500/20">
              <Clock className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Barra de Busca e Filtros */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-3 bg-card border border-border rounded-2xl">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por paciente, queixa clínica ou especialidade..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-muted/30 border border-border rounded-xl text-xs sm:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        <div className="flex items-center gap-2">
          {(['ALL', 'COMPLETED', 'CANCELLED'] as const).map((statusOption) => (
            <button
              key={statusOption}
              onClick={() => {
                setFilter(statusOption);
                setPage(1);
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${
                filter === statusOption
                  ? 'bg-primary text-primary-foreground shadow-xs'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
              }`}
            >
              {statusOption === 'ALL' && 'Todos'}
              {statusOption === 'COMPLETED' && 'Concluídas'}
              {statusOption === 'CANCELLED' && 'Canceladas'}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de Atendimentos */}
      {isLoading ? (
        <LoadingOverlay message="Carregando histórico de atendimentos..." />
      ) : isError ? (
        <ErrorRetry message="Erro ao carregar histórico" onRetry={() => refetch()} />
      ) : filteredConsultations.length === 0 ? (
        <EmptyState
          icon="FileText"
          title="Nenhum atendimento encontrado"
          description={search || filter !== 'ALL' ? 'Tente ajustar os filtros de pesquisa' : 'Seus atendimentos finalizados aparecerão aqui'}
          action={{ label: 'Recarregar', onClick: () => refetch() }}
        />
      ) : (
        <div className="space-y-3 sm:space-y-4">
          {filteredConsultations.map((consultation) => (
            <div
              key={consultation.id}
              className="p-5 sm:p-6 bg-card border border-border/80 rounded-2xl sm:rounded-3xl shadow-xs hover:shadow-md transition-all space-y-4"
            >
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                {/* Paciente e Detalhes */}
                <div className="flex items-start gap-3.5">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-primary/20 via-primary/10 to-primary/5 text-primary font-black text-sm flex items-center justify-center shrink-0 border border-primary/20 shadow-2xs">
                    {getInitials(consultation.patientName)}
                  </div>

                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-base sm:text-lg text-foreground">
                        {consultation.patientName}
                      </h3>
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                        {consultation.specialty}
                      </span>
                    </div>

                    <p className="text-xs text-muted-foreground mt-0.5">
                      {consultation.patientAge > 0 ? `${consultation.patientAge} anos` : 'Idade não informada'}
                      {consultation.reason ? ` • Queixa: ${consultation.reason}` : ''}
                    </p>

                    <div className="flex flex-wrap items-center gap-2.5 mt-2.5 text-xs text-muted-foreground">
                      <div className="flex items-center gap-1 bg-muted/40 px-2 py-0.5 rounded-lg border border-border/60">
                        <Calendar className="h-3 w-3 text-primary" />
                        <span>{formatDate(consultation.date)}</span>
                      </div>

                      <div className="flex items-center gap-1 bg-muted/40 px-2 py-0.5 rounded-lg border border-border/60">
                        <Clock className="h-3 w-3 text-primary" />
                        <span>{consultation.time}</span>
                      </div>

                      {consultation.status === 'COMPLETED' && (
                        <div className="flex items-center gap-1 bg-muted/40 px-2 py-0.5 rounded-lg border border-border/60 font-medium">
                          <Activity className="h-3 w-3 text-emerald-500" />
                          <span>{consultation.duration} min</span>
                        </div>
                      )}

                      {/* Modalidade */}
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                          consultation.type === 'EXPRESS'
                            ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400 border border-purple-500/20'
                            : consultation.type === 'HOME'
                            ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                            : consultation.type === 'OFFICE'
                            ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20'
                            : 'bg-primary/10 text-primary border border-primary/20'
                        }`}
                      >
                        {consultation.type === 'EXPRESS' && <Video className="w-3 h-3" />}
                        {consultation.type === 'HOME' && <Home className="w-3 h-3" />}
                        {consultation.type === 'OFFICE' && <MapPin className="w-3 h-3" />}
                        {consultation.type === 'SCHEDULED' && <Video className="w-3 h-3" />}
                        {consultation.type === 'EXPRESS' ? 'Plantão Express' : consultation.type === 'HOME' ? 'Domiciliar' : consultation.type === 'OFFICE' ? 'Consultório' : 'Telemedicina'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Preço e Status */}
                <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 border-t sm:border-t-0 pt-3 sm:pt-0 border-border">
                  <span
                    className={`px-3 py-1 text-xs font-bold rounded-full ${
                      consultation.status === 'COMPLETED'
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                        : 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20'
                    }`}
                  >
                    {consultation.status === 'COMPLETED' ? 'Concluída' : 'Cancelada'}
                  </span>

                  {consultation.status === 'COMPLETED' && (
                    <span className="font-extrabold text-emerald-600 dark:text-emerald-400 text-lg sm:text-xl">
                      {formatCurrency(consultation.earnings)}
                    </span>
                  )}
                </div>
              </div>

              {/* Botões de Ação Clínica Contextual */}
              {consultation.status === 'COMPLETED' && (
                <div className="pt-3 border-t border-border/80 flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => openModal(consultation, 'prontuario')}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-xl bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 transition-all cursor-pointer shadow-2xs hover:scale-[1.02]"
                    title="Ver prontuário e evolução clínica deste atendimento"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    <span>Prontuário (SOAP)</span>
                  </button>

                  <button
                    onClick={() => openModal(consultation, 'receitas')}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/20 transition-all cursor-pointer shadow-2xs hover:scale-[1.02]"
                    title="Ver receitas e prescrições emitidas nesta consulta"
                  >
                    <Receipt className="h-3.5 w-3.5" />
                    <span>Receitas MeMed</span>
                  </button>

                  <button
                    onClick={() => openModal(consultation, 'atestados')}
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-bold rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 border border-amber-500/20 transition-all cursor-pointer shadow-2xs hover:scale-[1.02]"
                    title="Ver atestados médicos e pedidos de exames"
                  >
                    <Award className="h-3.5 w-3.5" />
                    <span>Atestados & Exames</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Paginação */}
      <div className="flex items-center justify-center gap-2 pt-4">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page === 1}
          className="p-2 border border-border bg-card text-foreground rounded-xl hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="px-3.5 py-1.5 text-xs font-bold bg-primary text-primary-foreground rounded-xl shadow-xs">
          {page}
        </span>
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={page * 10 >= (appointmentsData?.pagination?.total || 0)}
          className="p-2 border border-border bg-card text-foreground rounded-xl hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Modal Contextual de Atendimento Clínico */}
      <Dialog open={!!selectedConsultation} onOpenChange={(open) => !open && setSelectedConsultation(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-6 rounded-3xl">
          <DialogHeader className="border-b border-border pb-3">
            <DialogTitle className="flex items-center gap-2.5 text-lg font-bold text-foreground">
              {modalTab === 'prontuario' && (
                <>
                  <div className="p-2 bg-primary/10 text-primary rounded-xl">
                    <FileText className="h-5 w-5" />
                  </div>
                  <span>Prontuário Clínico & Evolução</span>
                </>
              )}
              {modalTab === 'receitas' && (
                <>
                  <div className="p-2 bg-emerald-500/10 text-emerald-600 rounded-xl">
                    <Pill className="h-5 w-5" />
                  </div>
                  <span>Receitas Digitais MeMed</span>
                </>
              )}
              {modalTab === 'atestados' && (
                <>
                  <div className="p-2 bg-amber-500/10 text-amber-600 rounded-xl">
                    <Award className="h-5 w-5" />
                  </div>
                  <span>Atestados & Pedidos de Exames</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedConsultation && (
            <div className="space-y-5 pt-3">
              {/* Card de Identificação do Paciente e Consulta */}
              <div className="p-4 bg-muted/30 dark:bg-neutral-900/60 rounded-2xl border border-border space-y-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-xl bg-primary text-white font-bold text-xs flex items-center justify-center">
                      {getInitials(selectedConsultation.patientName)}
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-foreground">{selectedConsultation.patientName}</h4>
                      <p className="text-xs text-muted-foreground">
                        {selectedConsultation.patientAge > 0 ? `${selectedConsultation.patientAge} anos • ` : ''}
                        {selectedConsultation.specialty}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <span className="text-xs font-semibold text-foreground">
                      {formatDate(selectedConsultation.date)}
                    </span>
                    <p className="text-[11px] text-muted-foreground">{selectedConsultation.time} ({selectedConsultation.duration} min)</p>
                  </div>
                </div>

                {selectedConsultation.reason && (
                  <div className="pt-2 border-t border-border/60 text-xs">
                    <span className="font-bold text-foreground">Queixa Informada: </span>
                    <span className="text-muted-foreground">{selectedConsultation.reason}</span>
                  </div>
                )}
              </div>

              {/* Seletor de Abas Estilizado */}
              <div className="flex items-center gap-1.5 p-1 bg-muted/40 rounded-2xl border border-border">
                {([
                  { key: 'prontuario' as ModalTab, label: 'Prontuário', icon: FileText },
                  { key: 'receitas' as ModalTab, label: 'Receitas', icon: Receipt },
                  { key: 'atestados' as ModalTab, label: 'Atestados & Exames', icon: Award },
                ]).map(({ key, label, icon: Icon }) => (
                  <button
                    key={key}
                    onClick={() => setModalTab(key)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                      modalTab === key
                        ? 'bg-card text-foreground shadow-xs'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>

              {/* Conteúdo das Abas */}
              {isLoadingDetails ? (
                <div className="py-12 text-center text-sm text-muted-foreground flex flex-col items-center justify-center gap-2">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  <span>Carregando dados clínicos do atendimento...</span>
                </div>
              ) : (
                <>
                  {/* Aba: Prontuário */}
                  {modalTab === 'prontuario' && (
                    <div className="space-y-3.5">
                      {clinicalSummaryData?.medicalRecord ? (
                        <>
                          {clinicalSummaryData.medicalRecord.chiefComplaint && (
                            <div className="p-4 rounded-2xl bg-card border border-border space-y-1">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-primary block">
                                Queixa Principal
                              </span>
                              <p className="text-sm text-foreground leading-relaxed">
                                {clinicalSummaryData.medicalRecord.chiefComplaint}
                              </p>
                            </div>
                          )}

                          {clinicalSummaryData.medicalRecord.clinicalNotes && (
                            <div className="p-4 rounded-2xl bg-card border border-border space-y-1">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                                História da Moléstia Atual & Anamnese
                              </span>
                              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                                {clinicalSummaryData.medicalRecord.clinicalNotes}
                              </p>
                            </div>
                          )}

                          {clinicalSummaryData.medicalRecord.physicalExam && (
                            <div className="p-4 rounded-2xl bg-card border border-border space-y-1">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                                Exame Físico / Sinais Vitais
                              </span>
                              <p className="text-sm text-foreground leading-relaxed">
                                {clinicalSummaryData.medicalRecord.physicalExam}
                              </p>
                            </div>
                          )}

                          {clinicalSummaryData.medicalRecord.diagnosis && (
                            <div className="p-4 rounded-2xl bg-card border border-border space-y-1">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                                Hipótese Diagnóstica
                              </span>
                              <p className="text-sm font-semibold text-foreground leading-relaxed">
                                {clinicalSummaryData.medicalRecord.diagnosis}
                              </p>
                            </div>
                          )}

                          {clinicalSummaryData.medicalRecord.plan && (
                            <div className="p-4 rounded-2xl bg-card border border-border space-y-1">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground block">
                                Conduta & Plano Terapêutico
                              </span>
                              <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                                {clinicalSummaryData.medicalRecord.plan}
                              </p>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="py-12 text-center text-sm text-muted-foreground border border-dashed border-border rounded-2xl p-6">
                          <FileText className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                          <p className="font-semibold text-foreground">Nenhum prontuário registrado</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Não foram encontradas anotações clínicas preenchidas para esta consulta.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Aba: Receitas */}
                  {modalTab === 'receitas' && (
                    <div className="space-y-3">
                      {clinicalSummaryData?.prescriptions && clinicalSummaryData.prescriptions.length > 0 ? (
                        clinicalSummaryData.prescriptions.map((rx) => (
                          <div
                            key={rx.id}
                            className="p-4 bg-emerald-50/40 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-800/60 rounded-2xl space-y-2.5"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-black text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">
                                {rx.type || 'Receita Digital'}
                              </span>
                              <span className="text-[11px] text-muted-foreground font-medium">
                                {formatDateTime(rx.createdAt)}
                              </span>
                            </div>

                            {rx.items && (
                              <div className="p-3 bg-card rounded-xl border border-border text-xs text-foreground leading-relaxed">
                                <span className="font-bold block text-muted-foreground text-[10px] uppercase mb-0.5">
                                  Prescrição / Medicamentos:
                                </span>
                                {rx.items}
                              </div>
                            )}

                            <div className="flex items-center justify-between pt-1">
                              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/50 px-2.5 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-800">
                                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                                Assinada com ICP-Brasil
                              </span>

                              {rx.pdfUrl && (
                                <a
                                  href={rx.pdfUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline bg-primary/10 hover:bg-primary/20 px-3 py-1 rounded-xl transition-all"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                  <span>Abrir PDF Oficial</span>
                                </a>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-12 text-center text-sm text-muted-foreground border border-dashed border-border rounded-2xl p-6">
                          <Receipt className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                          <p className="font-semibold text-foreground">Nenhuma receita emitida</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Nenhuma prescrição ou receita médica foi gerada neste atendimento.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Aba: Atestados & Exames */}
                  {modalTab === 'atestados' && (
                    <div className="space-y-4">
                      {/* Atestados Médicos */}
                      {clinicalSummaryData?.certificates && clinicalSummaryData.certificates.length > 0 ? (
                        clinicalSummaryData.certificates.map((cert) => (
                          <div
                            key={cert.id}
                            className="p-4 bg-amber-50/40 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-800/60 rounded-2xl space-y-2.5"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-black text-amber-700 dark:text-amber-400 uppercase tracking-wider">
                                {cert.type || 'Atestado Médico'}
                              </span>
                              <span className="text-[11px] text-muted-foreground font-medium">
                                {formatDateTime(cert.createdAt)}
                              </span>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs">
                              {cert.cid10Code && (
                                <div className="p-2.5 bg-card rounded-xl border border-border">
                                  <span className="text-[10px] text-muted-foreground font-semibold block uppercase">
                                    Diagnóstico (CID-10)
                                  </span>
                                  <span className="font-bold text-foreground">{cert.cid10Code}</span>
                                </div>
                              )}

                              {cert.days && (
                                <div className="p-2.5 bg-card rounded-xl border border-border">
                                  <span className="text-[10px] text-muted-foreground font-semibold block uppercase">
                                    Período de Afastamento
                                  </span>
                                  <span className="font-bold text-foreground">{cert.days} dia(s)</span>
                                </div>
                              )}
                            </div>

                            <div className="flex items-center justify-between pt-1">
                              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/50 px-2.5 py-0.5 rounded-full border border-amber-200 dark:border-amber-800">
                                <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
                                Válido Nacionalmente
                              </span>

                              {cert.pdfUrl && (
                                <a
                                  href={cert.pdfUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1.5 text-xs font-bold text-primary hover:underline bg-primary/10 hover:bg-primary/20 px-3 py-1 rounded-xl transition-all"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                  <span>Abrir PDF Oficial</span>
                                </a>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="py-6 text-center text-xs text-muted-foreground border border-dashed border-border rounded-xl p-4">
                          Nenhum atestado emitido para esta consulta.
                        </div>
                      )}

                      {/* Pedidos de Exames */}
                      {clinicalSummaryData?.exams && clinicalSummaryData.exams.length > 0 && (
                        <div className="pt-2 space-y-2">
                          <span className="text-xs font-bold text-foreground uppercase tracking-wider block">
                            Pedidos de Exames Complementares
                          </span>

                          {clinicalSummaryData.exams.map((exam) => (
                            <div
                              key={exam.id}
                              className="p-3.5 bg-blue-50/40 dark:bg-blue-950/20 border border-blue-200/60 dark:border-blue-800/60 rounded-2xl flex items-center justify-between"
                            >
                              <div>
                                <p className="text-xs font-bold text-foreground">
                                  {exam.description || 'Pedido de Exame'}
                                </p>
                                <span className="text-[11px] text-muted-foreground">{formatDateTime(exam.createdAt)}</span>
                              </div>

                              {exam.pdfUrl && (
                                <a
                                  href={exam.pdfUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1 text-xs font-bold text-primary hover:underline"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  <span>Ver Laudo/Pedido</span>
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
